import { useEffect, useState } from "react";
import { Users, CalendarDays, Newspaper, HandCoins } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtMoney } from "@/lib/format";
import { CelebrationsBanner } from "@/components/admin/CelebrationsBanner";

export default function AdminDashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState({ members: 0, events: 0, news: 0, donations_count: 0, donations_total: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, e, n, d] = await Promise.all([
          api.get("/admin/members"),
          api.get("/events"),
          api.get("/news"),
          api.get("/admin/donations"),
        ]);
        if (cancelled) return;
        const paid = (d.data || []).filter((x) => x.payment_status === "paid");
        const total = paid.reduce((acc, x) => acc + Number(x.amount || 0), 0);
        setStats({
          members: m.data?.length || 0,
          events: e.data?.length || 0,
          news: n.data?.length || 0,
          donations_count: paid.length,
          donations_total: total,
        });
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const tiles = [
    { key: "members", icon: Users, value: stats.members, label: t.admin.stats.members, testid: "stat-members" },
    { key: "events", icon: CalendarDays, value: stats.events, label: t.admin.stats.events, testid: "stat-events" },
    { key: "news", icon: Newspaper, value: stats.news, label: t.admin.stats.news, testid: "stat-news" },
    { key: "donations", icon: HandCoins, value: `${stats.donations_count} \u00b7 ${fmtMoney(stats.donations_total)}`, label: t.admin.stats.donations, testid: "stat-donations" },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-serif-display text-2xl font-semibold">{t.admin.dashboard}</h1>
        <p className="text-sm text-muted-foreground">{t.admin.welcomeBack}</p>
      </div>
      <CelebrationsBanner />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Card key={tile.key} className="rounded-2xl ring-1 ring-black/5" data-testid={tile.testid}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{tile.label}</p>
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <p className="mt-2 font-serif-display text-2xl font-semibold">{tile.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
